import { api } from './api';
import { DashboardStatsResponse } from '../types';
import {
  BusinessStatusPeriod,
  DashboardWindowName,
  GaragesActivity,
  GaragesBusinessStatus,
  GaragesNeedsAction,
  OrdersBusinessStatus,
  OrdersDashboard,
  RefundsDashboard,
  ReviewsDashboard,
  UsersActivity,
  UsersBusinessStatus,
} from '../types/audit';

export function getDashboardStats(token: string): Promise<DashboardStatsResponse> {
  return api.get<DashboardStatsResponse>('/api/garages/dashboard/stats', token);
}

/** Spec 069 AC-A5 — every dashboard block gives up after 3 s and shows "Couldn't load · Retry". */
export const DASHBOARD_BLOCK_TIMEOUT_MS = 3000;

/**
 * Runs one abortable GET with the block timeout. An outer signal (component unmount, a newer
 * refresh) aborts it too. A timeout rejects with an Error named `TimeoutError`.
 */
export function withTimeout<T>(
  call: (signal: AbortSignal) => Promise<T>,
  outer?: AbortSignal,
  timeoutMs = DASHBOARD_BLOCK_TIMEOUT_MS,
): Promise<T> {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => { timedOut = true; controller.abort(); }, timeoutMs);
  const onOuterAbort = () => controller.abort();
  outer?.addEventListener('abort', onOuterAbort);
  return call(controller.signal)
    .catch((err) => {
      if (timedOut) {
        const e = new Error('Timed out');
        e.name = 'TimeoutError';
        throw e;
      }
      throw err;
    })
    .finally(() => {
      clearTimeout(timer);
      outer?.removeEventListener('abort', onOuterAbort);
    });
}

/** Spec 069 §2c — one aggregate per owning service; each is its own dashboard block. */
export const dashboardApi = {
  needsAction: (token: string, outer?: AbortSignal) =>
    withTimeout((s) => api.getAbortable<GaragesNeedsAction>('/api/garages/dashboard/needs-action', token, s), outer),

  activity: (window: DashboardWindowName, token: string, outer?: AbortSignal) =>
    withTimeout((s) => api.getAbortable<GaragesActivity>(`/api/garages/dashboard/activity?window=${window}`, token, s), outer),

  ordersDashboard: (window: DashboardWindowName, token: string, outer?: AbortSignal) =>
    withTimeout((s) => api.getAbortable<OrdersDashboard>(`/api/admin/orders/dashboard?window=${window}`, token, s), outer),

  refundsDashboard: (token: string, outer?: AbortSignal) =>
    withTimeout((s) => api.getAbortable<RefundsDashboard>('/api/admin/refunds/dashboard', token, s), outer),

  reviewsDashboard: (window: DashboardWindowName, token: string, outer?: AbortSignal) =>
    withTimeout((s) => api.getAbortable<ReviewsDashboard>(`/api/admin/reviews/dashboard?window=${window}`, token, s), outer),

  usersActivity: (window: DashboardWindowName, token: string, outer?: AbortSignal) =>
    withTimeout((s) => api.getAbortable<UsersActivity>(`/api/admin/users/dashboard/activity?window=${window}`, token, s), outer),

  /** Spec 070 — garages + services metrics (garages block feeds two tiles); #1331 `period`. */
  garagesBusinessStatus: (period: BusinessStatusPeriod, token: string, outer?: AbortSignal) =>
    withTimeout((s) => api.getAbortable<GaragesBusinessStatus>(`/api/garages/dashboard/business-status?period=${period}`, token, s), outer),

  /** Spec 070 — customers metric. */
  usersBusinessStatus: (period: BusinessStatusPeriod, token: string, outer?: AbortSignal) =>
    withTimeout((s) => api.getAbortable<UsersBusinessStatus>(`/api/admin/users/dashboard/business-status?period=${period}`, token, s), outer),

  /** Spec 070 — orders metric (the UI shows the period windows only, Maher c=B). */
  ordersBusinessStatus: (period: BusinessStatusPeriod, token: string, outer?: AbortSignal) =>
    withTimeout((s) => api.getAbortable<OrdersBusinessStatus>(`/api/admin/orders/dashboard/business-status?period=${period}`, token, s), outer),
};
